/**
 * @file ConfirmDialog - Modal confirmation gate for destructive/irreversible actions.
 *
 * Wraps MUI Dialog with a two-button (cancel / confirm) layout. Designed for
 * operations like deleting an absence, rejecting an expense, or removing a vehicle.
 *
 * Key behavior:
 *   - While `loading` is true, both buttons are disabled AND the backdrop close
 *     handler is removed (onClose set to undefined), preventing the user from
 *     dismissing the dialog mid-operation.
 *   - Default labels are in German ("Bestätigen" / "Abbrechen") to match the
 *     app's target audience.
 *   - `confirmColor` can be set to "error" for delete confirmations to give a
 *     clear visual warning.
 *
 * @example
 *   <ConfirmDialog
 *     open={showDelete}
 *     title="Eintrag löschen?"
 *     message="Dieser Vorgang kann nicht rückgängig gemacht werden."
 *     confirmLabel="Löschen"
 *     confirmColor="error"
 *     loading={isDeleting}
 *     onConfirm={handleDelete}
 *     onCancel={() => setShowDelete(false)}
 *   />
 */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Bestätigen',
  cancelLabel = 'Abbrechen',
  confirmColor = 'primary',
  onConfirm,
  onCancel,
  loading = false,
}) {
  return (
    <Dialog
      open={open}
      // Disable backdrop/escape-key close while the async action is in flight
      // to prevent the dialog from disappearing before the operation completes.
      onClose={loading ? undefined : onCancel}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id="confirm-dialog-title">
        {title}
      </DialogTitle>

      <DialogContent>
        <DialogContentText id="confirm-dialog-description">
          {message}
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onCancel}
          disabled={loading}
          color="inherit"
        >
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          color={confirmColor}
          variant="contained"
          autoFocus
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            confirmLabel
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ConfirmDialog.propTypes = {
  /** Controls dialog visibility */
  open: PropTypes.bool.isRequired,
  /** Dialog title */
  title: PropTypes.string.isRequired,
  /** Confirmation message */
  message: PropTypes.string.isRequired,
  /** Confirm button label */
  confirmLabel: PropTypes.string,
  /** Cancel button label */
  cancelLabel: PropTypes.string,
  /** Confirm button color */
  confirmColor: PropTypes.oneOf(['primary', 'secondary', 'error', 'warning', 'info', 'success']),
  /** Callback when user confirms */
  onConfirm: PropTypes.func.isRequired,
  /** Callback when user cancels */
  onCancel: PropTypes.func.isRequired,
  /** Show loading state on confirm button */
  loading: PropTypes.bool,
};

ConfirmDialog.defaultProps = {
  confirmLabel: 'Bestätigen',
  cancelLabel: 'Abbrechen',
  confirmColor: 'primary',
  loading: false,
};

export default ConfirmDialog;
